import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  Chip, 
  Grid,
  Button,
  Rating,
  Avatar,
  IconButton
} from '@mui/material';
import { 
  ArrowBack, 
  CalendarToday, 
  LocationOn, 
  Favorite,
  FavoriteBorder,
  People
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';

interface Campaign {
  id: string;
  title: string;
  company: string;
  deadline: string;
  location: string;
  reward: string;
  rating: number;
  applicants: number;
  maxApplicants: number;
  image: string;
  isLiked: boolean;
}

const mockCampaigns: Campaign[] = [
  {
    id: '1',
    title: '서울 강남구 맛집 탐방',
    company: '강남구청',
    deadline: '2024-02-28',
    location: '서울 강남구',
    reward: '500P',
    rating: 4.8,
    applicants: 23,
    maxApplicants: 30,
    image: '/api/placeholder/300/200',
    isLiked: true
  },
  {
    id: '2',
    title: '부산 해운대 카페 투어',
    company: '해운대구청',
    deadline: '2024-03-15',
    location: '부산 해운대구',
    reward: '400P',
    rating: 4.6,
    applicants: 18,
    maxApplicants: 25,
    image: '/api/placeholder/300/200',
    isLiked: true
  },
  {
    id: '3',
    title: '제주도 숙박 체험',
    company: '제주관광협회',
    deadline: '2024-04-30',
    location: '제주도',
    reward: '1000P',
    rating: 4.9,
    applicants: 8,
    maxApplicants: 15,
    image: '/api/placeholder/300/200',
    isLiked: true
  },
  {
    id: '4',
    title: '2024 뷰티 트렌드 리포트',
    company: '뷰티협회',
    deadline: '2024-02-20',
    location: '전국',
    reward: '800P',
    rating: 4.7,
    applicants: 45,
    maxApplicants: 50,
    image: '/api/placeholder/300/200',
    isLiked: true
  }
];

const ReviewerLikedCampaignsPage: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/mypage/reviewer');
  };

  const handleCampaignClick = (campaignId: string) => {
    navigate(`/campaign/${campaignId}`);
  };

  const handleLikeToggle = (campaignId: string) => {
    // TODO: Implement like toggle functionality
    console.log('Toggle like for campaign:', campaignId);
  };

  const handleApply = (campaignId: string) => {
    navigate(`/campaign/${campaignId}`);
  };

  return (
    <>
      <Header showIcons={true} />
      <Container maxWidth="md" sx={{ pb: 8, pt: 0 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            뒤로
          </Button>
          <Typography variant="h5" component="h1">
            찜한 캠페인
          </Typography>
        </Box>

      {/* Campaign List */}
      <Box>
        {mockCampaigns.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              찜한 캠페인이 없습니다
            </Typography>
            <Typography variant="body2" color="text.secondary">
              마음에 드는 캠페인을 찜해보세요!
            </Typography>
            <Button 
              variant="contained" 
              sx={{ mt: 2 }}
              onClick={() => navigate('/category')}
            >
              캠페인 둘러보기
            </Button>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {mockCampaigns.map((campaign) => (
              <Grid item xs={12} key={campaign.id}>
                <Card>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Avatar
                        src={campaign.image}
                        variant="rounded"
                        sx={{ width: 100, height: 100, cursor: 'pointer', flexShrink: 0 }}
                        onClick={() => handleCampaignClick(campaign.id)}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Typography 
                            variant="h6" 
                            component="h2" 
                            sx={{ 
                              fontWeight: 600,
                              fontSize: '1.1rem',
                              lineHeight: 1.3,
                              cursor: 'pointer',
                              mb: 1
                            }}
                            onClick={() => handleCampaignClick(campaign.id)}
                          >
                            {campaign.title}
                          </Typography>
                          <IconButton
                            onClick={() => handleLikeToggle(campaign.id)}
                            color="primary"
                            size="small"
                          >
                            {campaign.isLiked ? <Favorite /> : <FavoriteBorder />}
                          </IconButton>
                        </Box>
                        
                        <Typography 
                          variant="body1" 
                          color="text.secondary" 
                          sx={{ 
                            fontSize: '0.95rem',
                            fontWeight: 500,
                            mb: 2
                          }}
                        >
                          {campaign.company}
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationOn fontSize="small" color="action" />
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ fontSize: '0.9rem' }}
                            >
                              {campaign.location}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarToday fontSize="small" color="action" />
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ fontSize: '0.9rem' }}
                            >
                              마감일: {campaign.deadline}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <People fontSize="small" color="action" />
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ fontSize: '0.9rem' }}
                            >
                              {campaign.applicants}/{campaign.maxApplicants}명
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Rating value={campaign.rating} readOnly size="small" />
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ fontSize: '0.9rem', fontWeight: 500 }}
                            >
                              {campaign.rating}
                            </Typography>
                          </Box>
                          <Typography 
                            variant="h6" 
                            color="primary" 
                            sx={{ 
                              fontWeight: 700,
                              fontSize: '1.2rem'
                            }}
                          >
                            {campaign.reward}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button 
                            variant="outlined" 
                            size="small"
                            onClick={() => handleCampaignClick(campaign.id)}
                            sx={{ 
                              flex: 1,
                              fontSize: '0.9rem',
                              fontWeight: 500
                            }}
                          >
                            상세보기
                          </Button>
                          <Button 
                            variant="contained" 
                            size="small"
                            onClick={() => handleApply(campaign.id)}
                            sx={{ 
                              flex: 1,
                              fontSize: '0.9rem',
                              fontWeight: 500
                            }}
                          >
                            신청하기
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
      </Container>
    </>
  );
};

export default ReviewerLikedCampaignsPage; 